import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { formatCurrency, type Currency } from "@/lib/currency";
import {
  Package,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Store,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Activity,
  Flame,
  Sparkles,
} from "lucide-react";

interface Participant {
  id: string;
  name: string;
  email: string;
  mobile: string;
  has_paid: boolean;
}

interface SplitPaymentRequest {
  id: string;
  product_name: string;
  product_image_url: string | null;
  total_price: number;
  split_amount: number;
  deadline: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  participants: Participant[];
}

interface MerchantInfo {
  id: string;
  store_name: string;
  shopify_store_domain: string;
  notification_email: string | null;
  widget_installed?: boolean | null;
  subscription_status?: string;
  plan?: string;
  trial_ends_at?: string | null;
  currency?: string;
}

interface SubscriptionInfo {
  plan: string;
  status: string;
  daysRemaining: number | null;
  isTrialExpired: boolean;
  splitsThisMonth: number;
  splitLimit: number | null;
}

interface DashboardOverviewProps {
  requests: SplitPaymentRequest[];
  merchantInfo: MerchantInfo | null;
  subscription: SubscriptionInfo | null;
  onNavigateToTab: (tab: string, subView?: string) => void;
}

const ChangeIndicator = ({ 
  current, 
  previous, 
  inverted = false 
}: { 
  current: number; 
  previous: number; 
  inverted?: boolean;
}) => {
  if (previous === 0 && current === 0) return null;
  
  const change = previous === 0 ? (current > 0 ? 100 : 0) : Math.round(((current - previous) / previous) * 100);
  const isPositive = inverted ? change <= 0 : change >= 0;
  
  if (change === 0) return null;
  
  return (
    <span className={`text-xs flex items-center gap-0.5 ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
      {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {Math.abs(change)}%
    </span>
  );
};

export function DashboardOverview({ 
  requests, 
  merchantInfo, 
  subscription,
  onNavigateToTab 
}: DashboardOverviewProps) {
  const [period, setPeriod] = useState<"week" | "month" | "quarter" | "year" | "all">("month");
  
  // Commerce amounts always follow the connected store currency.
  const currency = ((merchantInfo?.currency || 'INR').toUpperCase() as Currency);

  // Payment streak calculation (consecutive days with payments)
  const paymentStreak = useMemo(() => {
    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 30; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      const dayStart = new Date(checkDate);
      const dayEnd = new Date(checkDate);
      dayEnd.setDate(dayEnd.getDate() + 1);
      const hasPayment = requests.some(r =>
        r.participants.some(p => p.has_paid) &&
        new Date(r.created_at) >= dayStart &&
        new Date(r.created_at) < dayEnd
      );
      if (hasPayment) { streak++; } else if (i > 0) { break; }
    }
    return streak;
  }, [requests]);

  const getRequestStatus = (status: string, deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    if (status === 'completed') return 'completed';
    if (status === 'expired' || now > deadlineDate) return 'expired';
    return 'pending';
  };

  // Period date calculations
  const getPeriodDates = (periodType: typeof period) => {
    const now = new Date();
    let startDate: Date;
    let prevStartDate: Date;
    let prevEndDate: Date;
    
    switch (periodType) {
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        prevEndDate = new Date(startDate.getTime() - 1);
        prevStartDate = new Date(prevEndDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        prevEndDate = new Date(startDate.getTime() - 1);
        prevStartDate = new Date(prevEndDate.getFullYear(), prevEndDate.getMonth(), 1);
        break;
      case 'quarter':
        const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
        startDate = new Date(now.getFullYear(), quarterMonth, 1);
        prevEndDate = new Date(startDate.getTime() - 1);
        const prevQuarterMonth = Math.floor(prevEndDate.getMonth() / 3) * 3;
        prevStartDate = new Date(prevEndDate.getFullYear(), prevQuarterMonth, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        prevEndDate = new Date(startDate.getTime() - 1);
        prevStartDate = new Date(prevEndDate.getFullYear(), 0, 1);
        break;
      case 'all':
      default:
        return { startDate: null, prevStartDate: null, prevEndDate: null };
    }
    
    return { startDate, prevStartDate, prevEndDate };
  };

  // All-time stats
  const allTimeStats = useMemo(() => ({
    totalRequests: requests.length,
    completed: requests.filter(r => getRequestStatus(r.status, r.deadline) === 'completed').length,
    pending: requests.filter(r => getRequestStatus(r.status, r.deadline) === 'pending').length,
    expired: requests.filter(r => getRequestStatus(r.status, r.deadline) === 'expired').length,
    totalCollected: requests.reduce((sum, r) => {
      const paidCount = r.participants.filter(p => p.has_paid).length;
      return sum + (paidCount * Number(r.split_amount));
    }, 0),
    pendingAmount: requests.reduce((sum, r) => {
      const unpaidCount = r.participants.filter(p => !p.has_paid).length;
      return sum + (unpaidCount * Number(r.split_amount));
    }, 0),
    totalParticipants: requests.reduce((sum, r) => sum + r.participants.length, 0),
    paidParticipants: requests.reduce((sum, r) => sum + r.participants.filter(p => p.has_paid).length, 0),
  }), [requests]);

  // Period-filtered stats
  const periodStats = useMemo(() => {
    const { startDate, prevStartDate, prevEndDate } = getPeriodDates(period);
    
    const filterByPeriod = (reqs: SplitPaymentRequest[], start: Date | null, end?: Date | null) => {
      if (!start) return reqs;
      return reqs.filter(r => {
        const date = new Date(r.created_at);
        if (end) return date >= start && date <= end;
        return date >= start;
      });
    };

    const periodReqs = filterByPeriod(requests, startDate);
    const prevReqs = prevStartDate && prevEndDate ? filterByPeriod(requests, prevStartDate, prevEndDate) : [];

    const calcStats = (reqs: SplitPaymentRequest[]) => {
      const completed = reqs.filter(r => getRequestStatus(r.status, r.deadline) === 'completed');
      return {
        total: reqs.length,
        completed: completed.length,
        pending: reqs.filter(r => getRequestStatus(r.status, r.deadline) === 'pending').length,
        expired: reqs.filter(r => getRequestStatus(r.status, r.deadline) === 'expired').length,
        collected: reqs.reduce((sum, r) => sum + (r.participants.filter(p => p.has_paid).length * Number(r.split_amount)), 0),
        participants: reqs.reduce((sum, r) => sum + r.participants.length, 0),
        paidParticipants: reqs.reduce((sum, r) => sum + r.participants.filter(p => p.has_paid).length, 0),
      };
    };

    return {
      current: calcStats(periodReqs),
      previous: calcStats(prevReqs),
    };
  }, [requests, period]);

  // Pending orders for action
  const pendingOrders = useMemo(() => {
    return requests
      .filter(r => getRequestStatus(r.status, r.deadline) === 'pending')
      .sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime())
      .slice(0, 5);
  }, [requests]);

  // Recent activity
  const recentPayments = useMemo(() => {
    const payments: { id: string; name: string; productName: string; amount: number }[] = [];
    requests.forEach(r => {
      r.participants.filter(p => p.has_paid).forEach(p => {
        payments.push({
          id: `${r.id}-${p.id}`,
          name: p.name,
          productName: r.product_name,
          amount: Number(r.split_amount),
        });
      });
    });
    return payments.slice(0, 4);
  }, [requests]);

  const getTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();
    if (diff <= 0) return "Expired";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 24) {
      return `${Math.floor(hours / 24)}d left`;
    }
    return `${hours}h left`;
  };

  const periodLabel = {
    week: "This Week",
    month: "This Month",
    quarter: "This Quarter",
    year: "This Year",
    all: "All Time",
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Welcome back{merchantInfo?.store_name ? `, ${merchantInfo.store_name}` : ''}!
          </h1>
          <p className="text-muted-foreground">
            Here's what's happening with your split payments today.
          </p>
        </div>
        {subscription?.plan === 'trial' && subscription.daysRemaining !== null && !subscription.isTrialExpired && (
          <Badge variant="secondary" className="w-fit">
            <Clock className="h-3 w-3 mr-1" />
            {subscription.daysRemaining} days left in trial
          </Badge>
        )}
      </div>

      {/* Insight Stats with Period Filter */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Key Metrics</h2>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as typeof period)}
          className="text-xs border border-input rounded-md px-3 py-1.5 bg-card text-foreground shadow-sm cursor-pointer hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="year">This Year</option>
          <option value="all">All Time</option>
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Revenue */}
        <Card className="relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigateToTab("insights")}>
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary/10" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Total Revenue</p>
                <p className="text-3xl font-bold tracking-tight">{formatCurrency(periodStats.current.collected, currency)}</p>
                <div className="flex items-center gap-1 mt-1">
                  <ArrowUpRight className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-500 font-medium">via Shakatah</span>
                </div>
              </div>
              <div className="p-3 bg-primary/10 rounded-full">
                <DollarSign className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conversion Rate */}
        <Card className="relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigateToTab("insights")}>
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-green-500/10" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Conversion Rate</p>
                <p className="text-3xl font-bold tracking-tight">
                  {periodStats.current.participants > 0
                    ? ((periodStats.current.paidParticipants / periodStats.current.participants) * 100).toFixed(1)
                    : '0.0'}%
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {periodStats.current.paidParticipants}/{periodStats.current.participants} participants
                </p>
              </div>
              <div className="p-3 bg-green-500/10 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Completed Splits */}
        <Card className="relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigateToTab("orders")}>
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-blue-500/10" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground font-medium">Completed Splits</p>
                <p className="text-3xl font-bold tracking-tight">{periodStats.current.completed}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  of {periodStats.current.total} total
                </p>
              </div>
              <div className="p-3 bg-blue-500/10 rounded-full">
                <CheckCircle2 className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Avg. Split Value</p>
              <p className="text-xl font-bold">
                {formatCurrency(
                  periodStats.current.completed > 0 
                    ? periodStats.current.collected / periodStats.current.completed 
                    : 0, 
                  currency
                )}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Pending Collection</p>
              <p className="text-xl font-bold text-yellow-600">
                {formatCurrency(
                  allTimeStats.pendingAmount,
                  currency
                )}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Total Participants</p>
              <p className="text-xl font-bold">{periodStats.current.participants}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Success Rate</p>
              <p className="text-xl font-bold text-green-600">
                {periodStats.current.total > 0 
                  ? Math.round((periodStats.current.completed / periodStats.current.total) * 100) 
                  : 0}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid - More balanced 3:2 layout */}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left Column - Analytics & Orders */}
        <div className="lg:col-span-3 space-y-6">
          {/* Period Performance */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    Performance Overview
                  </CardTitle>
                  <CardDescription>Track your orders and revenue</CardDescription>
                </div>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as typeof period)}
                  className="text-xs border border-input rounded-md px-3 py-1.5 bg-card text-foreground shadow-sm cursor-pointer hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                  <option value="all">All Time</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Orders Summary */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Orders Created</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{periodStats.current.total}</span>
                      <ChangeIndicator current={periodStats.current.total} previous={periodStats.previous.total} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Completed</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-600">{periodStats.current.completed}</span>
                      <ChangeIndicator current={periodStats.current.completed} previous={periodStats.previous.completed} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Conversion Rate</span>
                    <span className="font-semibold text-primary">
                      {periodStats.current.total > 0 
                        ? Math.round((periodStats.current.completed / periodStats.current.total) * 100) 
                        : 0}%
                    </span>
                  </div>
                </div>

                {/* Revenue Summary */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Revenue</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-green-600">{formatCurrency(periodStats.current.collected, currency)}</span>
                      <ChangeIndicator current={periodStats.current.collected} previous={periodStats.previous.collected} />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Participants Paid</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{periodStats.current.paidParticipants}/{periodStats.current.participants}</span>
                    </div>
                  </div>
                  <div className="pt-1">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span>Payment Progress</span>
                      <span>
                        {periodStats.current.participants > 0 
                          ? Math.round((periodStats.current.paidParticipants / periodStats.current.participants) * 100) 
                          : 0}%
                      </span>
                    </div>
                    <Progress 
                      value={periodStats.current.participants > 0 
                        ? (periodStats.current.paidParticipants / periodStats.current.participants) * 100 
                        : 0} 
                      className="h-2"
                    />
                  </div>
                </div>
              </div>

              {/* Status Breakdown */}
              <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-3">
                <div 
                  className="flex items-center justify-between p-2 rounded-lg bg-green-500/5 border border-green-500/20 cursor-pointer hover:bg-green-500/10 transition-colors"
                  onClick={() => onNavigateToTab("orders")}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Completed</span>
                  </div>
                  <span className="font-semibold text-green-600">{periodStats.current.completed}</span>
                </div>
                <div 
                  className="flex items-center justify-between p-2 rounded-lg bg-yellow-500/5 border border-yellow-500/20 cursor-pointer hover:bg-yellow-500/10 transition-colors"
                  onClick={() => onNavigateToTab("orders")}
                >
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm">Pending</span>
                  </div>
                  <span className="font-semibold text-yellow-600">{periodStats.current.pending}</span>
                </div>
                <div 
                  className="flex items-center justify-between p-2 rounded-lg bg-red-500/5 border border-red-500/20 cursor-pointer hover:bg-red-500/10 transition-colors"
                  onClick={() => onNavigateToTab("orders")}
                >
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm">Expired</span>
                  </div>
                  <span className="font-semibold text-red-600">{periodStats.current.expired}</span>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => onNavigateToTab("insights")} className="gap-1">
                  View detailed analytics
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Pending Orders Requiring Attention */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="h-5 w-5 text-yellow-600" />
                    Orders Awaiting Payment
                  </CardTitle>
                  <CardDescription>Active orders sorted by deadline</CardDescription>
                </div>
                {pendingOrders.length > 0 && (
                  <Badge variant="secondary">{allTimeStats.pending} total</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {pendingOrders.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
                  <p>No pending orders</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingOrders.map((order) => {
                    const { paid, total } = {
                      paid: order.participants.filter(p => p.has_paid).length,
                      total: order.participants.length,
                    };
                    const progress = (paid / total) * 100;
                    const timeLeft = getTimeRemaining(order.deadline);
                    const isUrgent = timeLeft.includes('h') && !timeLeft.includes('d');

                    return (
                      <div 
                        key={order.id} 
                        className={`p-3 rounded-lg border ${isUrgent ? 'border-yellow-500/50 bg-yellow-500/5' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{order.product_name}</p>
                            <p className="text-xs text-muted-foreground">{order.participants.length} participants</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(Number(order.total_price), currency)}</p>
                            <Badge 
                              variant={isUrgent ? "destructive" : "secondary"} 
                              className="text-xs"
                            >
                              {timeLeft}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Progress value={progress} className="h-1.5 flex-1" />
                          <span className="text-xs text-muted-foreground">{paid}/{total}</span>
                        </div>
                      </div>
                    );
                  })}
                  {allTimeStats.pending > 5 && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full" 
                      onClick={() => onNavigateToTab("orders")}
                    >
                      View all {allTimeStats.pending} pending orders
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Status & Quick Info */}
        <div className="lg:col-span-2 space-y-6">

          {/* Account Status */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Store className="h-5 w-5 text-primary" />
                Account Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Plan Status */}
              <div 
                className="p-3 rounded-lg bg-primary/5 border border-primary/20 cursor-pointer hover:bg-primary/10 transition-colors"
                onClick={() => onNavigateToTab("settings", "billing")}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Current Plan</span>
                  <Badge variant={subscription?.plan === 'trial' ? 'secondary' : 'default'}>
                    {subscription?.plan || 'Unknown'}
                  </Badge>
                </div>
              </div>

              {/* Split Usage */}
              {subscription && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Splits Used</span>
                    <span className="font-medium">
                      {subscription.splitsThisMonth}
                      {subscription.splitLimit && `/${subscription.splitLimit}`}
                    </span>
                  </div>
                  {subscription.splitLimit && (
                    <Progress 
                      value={(subscription.splitsThisMonth / subscription.splitLimit) * 100} 
                      className="h-2"
                    />
                  )}
                </div>
              )}

              {/* Store Info */}
              <div className="pt-2 border-t space-y-2">
                <div 
                  className="flex items-center justify-between text-sm p-2 -mx-2 rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => onNavigateToTab("settings", "store")}
                >
                  <span className="text-muted-foreground">Store</span>
                  <span className="font-medium truncate max-w-[150px]">{merchantInfo?.store_name || 'N/A'}</span>
                </div>
                <div 
                  className="flex items-center justify-between text-sm p-2 -mx-2 rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={() => onNavigateToTab("settings", "widget")}
                >
                  <span className="text-muted-foreground">Widget</span>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                    Active
                  </Badge>
                </div>
              </div>

              <Button 
                variant="outline" 
                size="sm" 
                className="w-full" 
                onClick={() => onNavigateToTab("settings", "billing")}
              >
                View your plan
              </Button>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          {recentPayments.length > 0 && (
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => onNavigateToTab("customers")}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="h-5 w-5 text-green-600" />
                  Recent Payments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recentPayments.map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-accent/50">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-6 h-6 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="h-3 w-3 text-green-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{payment.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{payment.productName}</p>
                        </div>
                      </div>
                      <span className="text-sm font-semibold text-green-600 flex-shrink-0">{formatCurrency(payment.amount, currency)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
